import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  DeleteSweep as DeleteSweepIcon
} from '@mui/icons-material';
import apiService from '../../services/apiService';
import { formatDate } from '../../utils/dateUtils';
import PaymentVoucherForm from './PaymentVoucherForm';

// Helper function to format date with Hijri format if needed
const formatHijriDate = (dateStr: string, format?: string) => {
  if (format === 'هـ') {
    // Simply append the format for demonstration (in a real app, actual conversion would be needed)
    return `${formatDate(dateStr)} هـ`;
  }
  return formatDate(dateStr);
};

interface PaymentVoucher {
  id: string;
  voucherNumber: string;
  number?: string;      // Added for fallback in TableCell
  reference?: string;   // Added for fallback in TableCell
  date: string;
  amount: number;
  debitAccount: {
    id: string;
    name: string;
  };
  creditAccount: {
    id: string;
    name: string;
  };
  description: string;
  status: string;
  createdBy: string;
  createdAt: string;
  beneficiary: string;
  paymentMethod: string;
  supplier?: string;    // For the supplier name
  user?: string;        // For the user who created the voucher
  vendor?: string;      // Added for fallback in supplier display
  expenseAccount?: string; // Added for fallback in account display
  accountName?: string;    // Added for fallback in account display
  creator?: string;        // Added for fallback in user display
  userName?: string;       // Added for fallback in user display
}

interface PaymentVouchersListProps {
  onRefresh?: () => void;
}

const PaymentVouchersList: React.FC<PaymentVouchersListProps> = ({ onRefresh }) => {
  const [vouchers, setVouchers] = useState<PaymentVoucher[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [currentVoucher, setCurrentVoucher] = useState<PaymentVoucher | null>(null);
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'create'>('create');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [voucherToDelete, setVoucherToDelete] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch payment vouchers from API
  const fetchVouchers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching payment vouchers from API...');
      // Try different endpoints in sequence until one works
      let response;
      const endpoints = [
        '/api/payment-vouchers',
        '/api/finance/payment-vouchers',
        '/api/financial/payment-vouchers',
        '/api/vouchers/payment'
      ];
      
      for (const endpoint of endpoints) {
        try {
          console.log(`Trying ${endpoint} endpoint...`);
          response = await apiService.get(endpoint);
          console.log(`Response from ${endpoint}:`, response);
          if (response) break;
        } catch (err) {
          console.error(`Error fetching from ${endpoint}:`, err);
        }
      }
      
      // If no endpoints worked, try the system status endpoint to check connectivity
      if (!response) {
        try {
          await apiService.get('/api/system/status');
          // If this works, the API is up but payment endpoints are not available
          console.error('API is available but payment voucher endpoints are not responding');
          setError('خطأ في الوصول إلى بيانات سندات الصرف. الخادم متصل ولكن نقاط النهاية غير متاحة.');
        } catch (err) {
          // If this fails too, there's likely no connectivity
          console.error('API connection failed completely:', err);
          setError('فشل الاتصال بالخادم. يرجى التحقق من اتصال الشبكة.');
        }
        setVouchers([]);
        setLoading(false);
        return;
      }
      
      // Process the response, supporting multiple formats
      let vouchersData: PaymentVoucher[] = [];
      
      if (Array.isArray(response)) {
        vouchersData = response;
      } else if (response && typeof response === 'object') {
        // Check for common response wrapper patterns
        if (Array.isArray(response.vouchers)) {
          vouchersData = response.vouchers;
        } else if (Array.isArray(response.data)) {
          vouchersData = response.data;
        } else if (Array.isArray(response.items)) {
          vouchersData = response.items;
        } else if (Array.isArray(response.results)) {
          vouchersData = response.results;
        } else if (Array.isArray(response.paymentVouchers)) {
          vouchersData = response.paymentVouchers;
        } else {
          console.error('Cannot find vouchers array in response:', response);
          setError('تنسيق بيانات غير متوقع من الخادم');
          setVouchers([]);
          setLoading(false);
          return;
        }
      } else {
        console.error('Unexpected response type:', response);
        setError('نوع استجابة غير متوقع من الخادم');
        setVouchers([]);
        setLoading(false);
        return;
      }
      
      console.log(`Payment vouchers loaded: ${vouchersData.length} items`);
      setVouchers(vouchersData);
    } catch (error) {
      console.error('Unhandled error fetching payment vouchers:', error);
      setError('فشل في جلب سندات الصرف. حدث خطأ غير متوقع.');
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  };

  // Load vouchers on component mount
  useEffect(() => {
    fetchVouchers();
  }, []);

  // Handle create/update/delete operations
  const handleCreate = () => {
    setCurrentVoucher(null);
    setModalMode('create');
    setModalVisible(true);
  };

  const handleEdit = (voucher: PaymentVoucher) => {
    setCurrentVoucher(voucher);
    setModalMode('edit');
    setModalVisible(true);
  };

  const handleView = (voucher: PaymentVoucher) => {
    setCurrentVoucher(voucher);
    setModalMode('view');
    setModalVisible(true);
  };

  const handleRefresh = () => {
    fetchVouchers();
    if (onRefresh) onRefresh();
  };

  const handleDeleteClick = (id: string) => {
    setVoucherToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!voucherToDelete) return;
    
    try {
      await apiService.delete(`/api/payment-vouchers/${voucherToDelete}`);
      fetchVouchers();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting payment voucher:', error);
      setError('فشل في حذف سند الصرف');
    } finally {
      setDeleteDialogOpen(false);
      setVoucherToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setVoucherToDelete(null);
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      if (newStatus === 'مؤكد') {
        await apiService.post(`/api/payment-vouchers/${id}/approve`);
      } else if (newStatus === 'مرفوض') {
        await apiService.post(`/api/payment-vouchers/${id}/reject`);
      } else {
        await apiService.put(`/api/payment-vouchers/${id}/status`, { status: newStatus });
      }
      
      fetchVouchers();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating payment voucher status:', error);
      setError('فشل في تحديث حالة سند الصرف');
    }
  };

  const handleFormSubmit = async (values: any) => {
    try {
      if (modalMode === 'create') {
        await apiService.post('/api/payment-vouchers', values);
      } else if (modalMode === 'edit' && currentVoucher) {
        await apiService.put(`/api/payment-vouchers/${currentVoucher.id}`, values);
      }
      
      setModalVisible(false);
      // Force refresh after create/update
      setTimeout(() => {
        fetchVouchers();
        if (onRefresh) onRefresh();
      }, 300);
    } catch (error) {
      console.error('Error submitting payment voucher form:', error);
      setError('فشل في حفظ سند الصرف');
    }
  };

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'مؤكد':
        return 'success';
      case 'مرفوض':
        return 'error';
      default:
        return 'warning';
    }
  };

  // Create test payment voucher data for debugging purposes
  const createTestVoucher = async () => {
    try {
      // Sample data with required fields
      const testVoucher = {
        debitAccount: "64f1e2a7d5e94c9a3b1234", // This should be a valid account ID
        creditAccount: "64f1e2a7d5e94c9a3b5678", // This should be a valid account ID
        amount: 1500,
        description: "سند صرف اختبار",
        date: new Date().toISOString(),
        beneficiary: "شركة الاختبار",
        paymentMethod: "نقدي"
      };
      
      await apiService.post('/api/payment-vouchers', testVoucher);
      fetchVouchers();
    } catch (error) {
      console.error('Error creating test payment voucher:', error);
      setError('فشل في إنشاء سند صرف اختبار');
    }
  };

  // Delete specific vouchers by ID
  const deleteSpecificVouchers = async () => {
    const voucherIdsToDelete = ['PAY-2025-001', 'PAY-2025-002', 'PAY-2025-003'];
    setLoading(true);
    setError(null);
    
    try {
      // Process each voucher deletion sequentially
      for (const voucherId of voucherIdsToDelete) {
        try {
          // Find the voucher by voucherNumber/ID in the current list
          const voucher = vouchers.find(v =>
            v.voucherNumber === voucherId ||
            v.number === voucherId ||
            v.reference === voucherId ||
            v.id === voucherId
          );
          
          if (voucher) {
            await apiService.delete(`/api/payment-vouchers/${voucher.id}`);
            console.log(`Deleted voucher ${voucherId}`);
          } else {
            console.warn(`Voucher ${voucherId} not found in current list`);
          }
        } catch (err) {
          console.error(`Error deleting voucher ${voucherId}:`, err);
        }
      }
      
      // Refresh the list after all delete operations
      fetchVouchers();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error in batch delete operation:', error);
      setError('فشل في حذف السندات المحددة');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" component="h2">
          سندات الصرف
        </Typography>
        <Box>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={deleteSpecificVouchers}
            sx={{ mr: 1 }}
          >
            حذف سندات 2025
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ mr: 1 }}
          >
            تحديث
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            إضافة سند صرف جديد
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>رقم السند</TableCell>
                <TableCell>التاريخ</TableCell>
                <TableCell>المورد</TableCell>
                <TableCell>الحساب المدين</TableCell>
                <TableCell>المبلغ (ريال)</TableCell>
                <TableCell>الحالة</TableCell>
                <TableCell>المستخدم</TableCell>
                <TableCell>الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      جاري تحميل البيانات...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : vouchers.length > 0 ? (
                vouchers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((voucher, index) => (
                    <TableRow key={voucher.id || `voucher-${index}`}>
                      <TableCell>{voucher.voucherNumber || voucher.number || voucher.reference || "---"}</TableCell>
                      <TableCell>{`${formatDate(voucher.date || voucher.createdAt || new Date().toISOString())} هـ`}</TableCell>
                      <TableCell>{voucher.supplier || voucher.beneficiary || voucher.vendor || "غير محدد"}</TableCell>
                      <TableCell>{voucher.debitAccount?.name || voucher.expenseAccount || voucher.accountName || "مصاريف عامة"}</TableCell>
                      <TableCell>
                        <span style={{ color: '#e53935', display: 'flex', alignItems: 'center' }}>
                          {(voucher.amount ? Number(voucher.amount) : 0).toLocaleString('ar-SA')} <span style={{ marginRight: '3px' }}>↑</span>
                        </span>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Normalize status
                          let status = voucher.status || 'تحت الموافقة';
                          if (status === 'approved' || status === 'confirmed' || status === 'موافق') status = 'مؤكد';
                          if (status === 'pending' || status === 'in_review') status = 'تحت الموافقة';
                          if (status === 'rejected' || status === 'declined' || status === 'canceled') status = 'مرفوض';
                          
                          return (
                            <Chip
                              label={status}
                              color={getStatusColor(status) as "success" | "error" | "warning"}
                              size="small"
                              sx={{
                                backgroundColor:
                                  status === 'مؤكد' ? '#26c6da' :
                                  status === 'تحت الموافقة' ? '#ffb74d' :
                                  '#f44336',
                                color: 'white'
                              }}
                              icon={
                                status === 'مؤكد' ? <CheckCircleIcon fontSize="small" /> :
                                status === 'تحت الموافقة' ? undefined :
                                <CancelIcon fontSize="small" />
                              }
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>{voucher.createdBy || voucher.user || voucher.creator || voucher.userName || "غير معروف"}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Tooltip title="المزيد">
                            <IconButton size="small">
                              <span style={{ fontSize: '16px', fontWeight: 'bold' }}>⋮</span>
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="طباعة">
                            <IconButton size="small" color="inherit">
                              <span role="img" aria-label="طباعة">🖨️</span>
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="تعديل">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(voucher)}
                              disabled={voucher.status !== 'تحت الموافقة'}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="عرض التفاصيل">
                            <IconButton size="small" color="primary" onClick={() => handleView(voucher)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" gutterBottom>
                      لا توجد سندات صرف
                    </Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={handleCreate}
                      sx={{ mr: 1 }}
                    >
                      إنشاء سند جديد
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={createTestVoucher}
                    >
                      إنشاء سند اختبار
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {vouchers.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={vouchers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="عدد السندات في الصفحة:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
          />
        )}
      </Paper>

      {modalVisible && (
        <PaymentVoucherForm
          visible={modalVisible}
          mode={modalMode}
          initialValues={currentVoucher}
          onSubmit={handleFormSubmit}
          onCancel={() => setModalVisible(false)}
        />
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من رغبتك في حذف سند الصرف هذا؟
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="inherit">
            إلغاء
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            حذف
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentVouchersList;